import {
	NewspaperIcon,
	PencilIcon,
	RectangleGroupIcon,
	TrashIcon,
} from "@heroicons/react/16/solid";
import { ArrowsUpDownIcon } from "@heroicons/react/24/outline";
import {
	Link,
	type LinkProps,
	MatchRoute,
	useRouteContext,
} from "@tanstack/react-router";
import { AnimatePresence, LayoutGroup, motion } from "motion/react";
import { Collapsible, ScrollArea } from "radix-ui";
import { Suspense, forwardRef, lazy, useState } from "react";
import { graphql, useFragment } from "react-relay";
import { Icon } from "@/components/icon";
import { Button } from "@/components/ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { spaceCreateDialog_clubFragment$key } from "@/dialogs/__generated__/spaceCreateDialog_clubFragment.graphql";
import type { spaceCreateDialog_spaceFragment$key } from "@/dialogs/__generated__/spaceCreateDialog_spaceFragment.graphql";
import type { spaceDeleteDialogFragment$key } from "@/dialogs/__generated__/spaceDeleteDialogFragment.graphql";
const CreateGroupDialog = lazy(() =>
	import("@/dialogs/group-create-dialog").then((m) => ({
		default: m.CreateGroupDialog,
	})),
);
const DeleteGroupDialog = lazy(() =>
	import("@/dialogs/group-delete-dialog").then((m) => ({
		default: m.DeleteGroupDialog,
	})),
);
const CreateSpaceDialog = lazy(() =>
	import("@/dialogs/space-create-dialog").then((m) => ({
		default: m.CreateSpaceDialog,
	})),
);
const DeleteSpaceDialog = lazy(() =>
	import("@/dialogs/space-delete-dialog").then((m) => ({
		default: m.DeleteSpaceDialog,
	})),
);
const FeedReorderDialog = lazy(() =>
	import("@/dialogs/feed-reorder-dialog").then((m) => ({
		default: m.FeedReorderDialog,
	})),
);
import { cn } from "@/utils/cn";
import type {
	communitySidebarFragment$data,
	communitySidebarFragment$key,
} from "./__generated__/communitySidebarFragment.graphql";
import { Drawer, DrawerTrigger } from "@/components/ui/drawer";
import type { feedReorderDialog_feed$key } from "@/dialogs/__generated__/feedReorderDialog_feed.graphql";

const AnimatedDropdownButton = motion.create(Button);

export const Space = forwardRef(function Space(
	{
		id,
		current,
		space,
		club: clubFragment,
		group,
		className,
		children,
		enableActions,
		...props
	}: {
		id: string;
		current?: boolean;
		space?: spaceCreateDialog_spaceFragment$key & spaceDeleteDialogFragment$key;
		club: spaceCreateDialog_clubFragment$key;
		group?: { id: string; name: string };
		className?: string;
		children: React.ReactNode;
		enableActions?: boolean;
	} & LinkProps,
	ref: React.ForwardedRef<HTMLAnchorElement>,
) {
	const { club } = useRouteContext({ from: "__root__" });

	const isAdmin =
		club?.permissions?.some((p) => p.slug === "CLUB_ADMIN") || false;

	const [dropdownMenuOpen, setDropdownMenuOpen] = useState(false);
	const [isHovered, setIsHovered] = useState(false);
	const [updateSpaceDialogOpen, setUpdateSpaceDialogOpen] = useState(false);
	const [deleteSpaceDialogOpen, setDeleteSpaceDialogOpen] = useState(false);

	const classes = cn(
		// Base
		"relative flex w-full items-center gap-3 rounded-md px-2 py-2.5 text-left text-base/6 font-medium text-sidebar-foreground sm:py-2 sm:text-sm/5",
		"data-[current=true]:bg-primary data-[current=true]:text-primary-foreground",
		"data-[current=false]:hover:text-accent-foreground",
	);

	return (
		<>
			{isAdmin && enableActions && !!group && (
				<>
					{updateSpaceDialogOpen && (
						<Suspense fallback={null}>
							<CreateSpaceDialog
								open={updateSpaceDialogOpen}
								onClose={() => setUpdateSpaceDialogOpen(false)}
								space={space}
								club={clubFragment}
								group={group}
							/>
						</Suspense>
					)}

					{Boolean(space) && deleteSpaceDialogOpen && (
						<Suspense fallback={null}>
							<DeleteSpaceDialog
								open={deleteSpaceDialogOpen}
								onClose={() => setDeleteSpaceDialogOpen(false)}
								space={space!}
							/>
						</Suspense>
					)}
				</>
			)}

			<li
				className={cn(
					"relative flex flex-row items-center justify-between group hover:text-accent-foreground",
					className,
				)}
				onMouseEnter={() => setIsHovered(true)}
				onMouseLeave={() => setIsHovered(false)}
			>
				{isHovered && (
					<motion.div
						layoutId="current-hover"
						className="absolute inset-0 w-full h-full bg-accent rounded-md pointer-events-none"
					/>
				)}

				<AnimatePresence mode="sync">
					<Link
						{...props}
						className={classes}
						data-current={current ? "true" : "false"}
						ref={ref}
					>
						{children}
					</Link>

					{isAdmin && enableActions && (isHovered || dropdownMenuOpen) ? (
						<DropdownMenu
							open={dropdownMenuOpen}
							onOpenChange={setDropdownMenuOpen}
						>
							<DropdownMenuTrigger asChild>
								<AnimatedDropdownButton
									id={`dropdown-${id}`}
									size="icon"
									variant="ghost"
									className="z-2"
									transition={{ ease: "easeInOut" }}
								>
									<Icon name="EllipsisVertical" className="size-4" />
								</AnimatedDropdownButton>
							</DropdownMenuTrigger>

							<DropdownMenuContent>
								<DropdownMenuItem
									onClick={() => setUpdateSpaceDialogOpen(true)}
								>
									<PencilIcon />
									Editar Feed
								</DropdownMenuItem>
								<DropdownMenuItem
									onClick={() => setDeleteSpaceDialogOpen(true)}
								>
									<TrashIcon />
									Deletar Feed
								</DropdownMenuItem>
							</DropdownMenuContent>
						</DropdownMenu>
					) : null}
				</AnimatePresence>
			</li>
		</>
	);
});

export function Group({
	group,
	club: clubFragment,
}: {
	group: communitySidebarFragment$data["groups"][number];
	club: spaceCreateDialog_clubFragment$key & feedReorderDialog_feed$key;
}) {
	const { club } = useRouteContext({ from: "__root__" });

	const isAdmin =
		club?.permissions?.some((p) => p.slug === "CLUB_ADMIN") || false;

	const [isHoveringName, setIsHoveringName] = useState(false);

	const [createFeedDialogOpen, setCreateFeedDialogOpen] = useState(false);
	const [createGroupDialogOpen, setCreateGroupDialogOpen] = useState(false);
	const [updateGroupDialogOpen, setUpdateGroupDialogOpen] = useState(false);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

	const [plusPopoverOpen, setPlusPopoverOpen] = useState(false);
	const [morePopoverOpen, setMorePopoverOpen] = useState(false);

	const [spacesOpen, setSpacesOpen] = useState(true);

	const visibleSpaces = group.spaces
		.filter(Boolean)
		.filter((s) => isAdmin || s.visibility !== "SECRET" || s.isMember)
		.sort((a, b) => a.position - b.position);

	if (visibleSpaces.length === 0 && !isAdmin) {
		return null;
	}

	return (
		<>
			{isAdmin && (
				<>
					{updateGroupDialogOpen && (
						<Suspense fallback={null}>
							<CreateGroupDialog
								open={updateGroupDialogOpen}
								onClose={() => setUpdateGroupDialogOpen(false)}
								group={group}
							/>
						</Suspense>
					)}

					{deleteDialogOpen && (
						<Suspense fallback={null}>
							<DeleteGroupDialog
								open={deleteDialogOpen}
								onClose={() => setDeleteDialogOpen(false)}
								group={group}
							/>
						</Suspense>
					)}

					{createFeedDialogOpen && (
						<Suspense fallback={null}>
							<CreateSpaceDialog
								open={createFeedDialogOpen}
								onClose={() => setCreateFeedDialogOpen(false)}
								group={group}
								club={clubFragment}
							/>
						</Suspense>
					)}

					{createGroupDialogOpen && (
						<Suspense fallback={null}>
							<CreateGroupDialog
								open={createGroupDialogOpen}
								onClose={() => setCreateGroupDialogOpen(false)}
							/>
						</Suspense>
					)}
				</>
			)}

			<div className="mt-6">
				{/* biome-ignore lint/a11y/noStaticElementInteractions: div not interactable */}
				<Collapsible.Root open={spacesOpen} onOpenChange={setSpacesOpen}>
					{/* biome-ignore lint/a11y/noStaticElementInteractions: div is static */}
					<div
						className="flex flex-row items-center justify-start"
						onMouseEnter={isAdmin ? () => setIsHoveringName(true) : undefined}
						onMouseLeave={isAdmin ? () => setIsHoveringName(false) : undefined}
					>
						<Collapsible.Trigger className="flex flex-row items-center justify-center">
							<Icon
								name="ChevronDown"
								className={cn(
									"size-3 -translate-y-px",
									!spacesOpen && "rotate-180",
								)}
							/>

							<h3 className="px-2 font-medium text-xs/6 text-sidebar-text/60 dark:text-sidebar-text/70">
								{group.name}
							</h3>
						</Collapsible.Trigger>

						{isAdmin && (
							<AnimatePresence>
								{(isHoveringName || plusPopoverOpen || morePopoverOpen) && (
									<motion.div
										id={`update-${group.id}`}
										animate={{ x: 0, opacity: 1 }}
										initial={{ x: -10, opacity: 0 }}
										exit={{ x: -10, opacity: 0 }}
										className="flex-row items-center gap-3 flex max-md:hidden dark:text-white/80"
									>
										<Drawer direction="right" data-vaul-no-drag>
											<DropdownMenu
												open={morePopoverOpen}
												onOpenChange={setMorePopoverOpen}
											>
												<DropdownMenuTrigger className="cursor-pointer">
													<Icon name="EllipsisVertical" className="size-3" />
												</DropdownMenuTrigger>

												<DropdownMenuContent>
													<DropdownMenuItem
														onClick={() => setUpdateGroupDialogOpen(true)}
													>
														<PencilIcon />
														Editar Grupo de Feeds
													</DropdownMenuItem>

													<DropdownMenuItem
														onClick={() => setDeleteDialogOpen(true)}
													>
														<TrashIcon />
														Deletar Grupo de Feeds
													</DropdownMenuItem>
												</DropdownMenuContent>
											</DropdownMenu>

											<DropdownMenu
												open={plusPopoverOpen}
												onOpenChange={setPlusPopoverOpen}
											>
												<DropdownMenuTrigger className="cursor-pointer">
													<Icon name="Plus" className="size-3" />
												</DropdownMenuTrigger>

												<DropdownMenuContent>
													<DropdownMenuItem
														onClick={() => setCreateFeedDialogOpen(true)}
													>
														<NewspaperIcon />
														Criar Feed
													</DropdownMenuItem>

													<DrawerTrigger asChild>
														<DropdownMenuItem>
															<ArrowsUpDownIcon />
															Reordenar Feeds
														</DropdownMenuItem>
													</DrawerTrigger>

													<DropdownMenuItem
														onClick={() => setCreateGroupDialogOpen(true)}
													>
														<RectangleGroupIcon />
														Criar Grupo de Feeds
													</DropdownMenuItem>
												</DropdownMenuContent>
											</DropdownMenu>
											<Suspense fallback={null}>
												<FeedReorderDialog club={clubFragment} />
											</Suspense>
										</Drawer>
									</motion.div>
								)}
							</AnimatePresence>
						)}
					</div>

					<Collapsible.Content>
						<LayoutGroup id={group.id}>
							<ul>
								{visibleSpaces.map((s) => (
									<MatchRoute
										key={s.id}
										to="/comunidade/s/$space"
										params={{ space: s.slug }}
									>
										{(match) => (
											<Space
												id={s.id}
												enableActions={true}
												to="/comunidade/s/$space"
												params={{ space: s.slug }}
												current={Boolean(match)}
												group={{ id: group.id, name: group.name }}
												club={clubFragment}
												space={s}
											>
												<div className="flex flex-row w-full items-center justify-between">
													<span>{`${s.icon || ""} ${s.name}`.trim()}</span>

													{(isAdmin || !s.isMember) &&
													s.visibility === "PRIVATE" ? (
														<Icon name="Lock" className="size-3" />
													) : null}

													{isAdmin && s.visibility === "SECRET" ? (
														<Icon name="EyeOff" className="size-3" />
													) : null}
												</div>
											</Space>
										)}
									</MatchRoute>
								))}
							</ul>
						</LayoutGroup>
					</Collapsible.Content>
				</Collapsible.Root>
			</div>
		</>
	);
}

export function CommunitySidebarContent({
	club: clubFragment,
}: {
	club: communitySidebarFragment$key;
}) {
	const data = useFragment(
		graphql`
     fragment communitySidebarFragment on Club {
       groups {
         id
         name
         spaces {
           id
           icon
           name
           slug
           visibility
           isMember
           position
           ...spaceCreateDialog_spaceFragment
           ...spaceDeleteDialogFragment
         }

         ...groupCreateDialog_updateFragment
         ...groupDeleteDialogFragment
       }

       ...spaceCreateDialog_clubFragment
       ...feedReorderDialog_feed
     }
   `,
		clubFragment,
	);

	return (
		<div className={cn("px-3 py-3")}>
			<MatchRoute to="/comunidade">
				{(match) => (
					<ul>
						{/* biome-ignore lint/correctness/useUniqueElementIds: feed is static*/}
						<Space
							current={Boolean(match)}
							to="/comunidade"
							id="feed"
							enableActions={false}
							club={data}
						>
							<Icon name="Newspaper" className="size-4" />
							Timeline
						</Space>
					</ul>
				)}
			</MatchRoute>

			{data.groups.filter(Boolean).map((group) => (
				<Group key={group.id} group={group} club={data} />
			))}
		</div>
	);
}

export function CommunitySidebar({
	club: clubFragment,
}: {
	club: communitySidebarFragment$key;
}) {
	const { club } = useRouteContext({ from: "__root__" });

	const isAdmin = (club?.permissions?.length || 0) > 0;

	return (
		<>
			<div className="w-[280px] h-full max-lg:hidden">&nbsp;</div>

			<div
				className={cn(
					"fixed border-r w-[280px] overflow-hidden max-lg:hidden",
					isAdmin ? "h-[calc(100dvh-60px-2rem)]" : "h-[calc(100dvh-60px)]",
				)}
			>
				<ScrollArea.Root className="size-full overflow-hidden bg-background">
					<ScrollArea.Viewport className="size-full">
						<CommunitySidebarContent club={clubFragment} />
					</ScrollArea.Viewport>

					<ScrollArea.Scrollbar
						className="flex touch-none select-none bg-zinc-100 dark:bg-zinc-800 p-0.5 transition-colors duration-[160ms] ease-out hover:bg-zinc-200 dark:hover:bg-zinc-700 data-[orientation=horizontal]:h-2 data-[orientation=vertical]:w-2 data-[orientation=horizontal]:flex-col"
						orientation="vertical"
					>
						<ScrollArea.Thumb className="relative flex-1 rounded-[10px] bg-zinc-300 before:absolute before:left-1/2 before:top-1/2 before:size-full before:min-h-11 before:min-w-11 before:-translate-x-1/2 before:-translate-y-1/2" />
					</ScrollArea.Scrollbar>
				</ScrollArea.Root>
			</div>
		</>
	);
}
